import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Code2, Database, Smartphone, Cloud, Palette, Shield, CheckCircle2 } from 'lucide-react';
import FAQSection from "../components/FAQSection";
import { hiredevelopersFaqs } from "../data/faqs";
import BlogSection from "../components/BlogSection"; // adjust path if needed
export const HireDevelopers = () => {
  const developers = [
    { icon: Code2, title: 'Frontend Developers', skills: ['React', 'Vue.js', 'Angular', 'TypeScript', 'Next.js'], description: 'Build stunning, responsive user interfaces with modern frameworks' },
    { icon: Database, title: 'Backend Developers', skills: ['Node.js', 'Python', 'Java', 'Go', 'Ruby'], description: 'Robust server-side solutions with scalable architecture' },
    { icon: Smartphone, title: 'Mobile Developers', skills: ['React Native', 'Flutter', 'iOS', 'Android', 'Kotlin'], description: 'Native and cross-platform mobile apps that users love' },
    { icon: Cloud, title: 'Full-Stack Developers', skills: ['MERN', 'MEAN', 'Django', 'Laravel', 'Ruby on Rails'], description: 'End-to-end development from database to user interface' },
    { icon: Palette, title: 'UI/UX Designers', skills: ['Figma', 'Adobe XD', 'Sketch', 'Prototyping', 'User Research'], description: 'Create beautiful, intuitive designs that drive conversions' },
    { icon: Shield, title: 'DevOps Engineers', skills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Terraform'], description: 'Streamline deployment and infrastructure management' }
  ];

  const benefits = [
    'Pre-vetted senior developers',
    'Flexible engagement models',
    'Timezone-aligned communication',
    'Direct access to developers',
    'Agile development process',
    'No hiring overhead',
    'Scale team up or down',
    'Transparent pricing'
  ];

  const hiringProcess = [
    { step: '1', title: 'Share Requirements', description: 'Tell us about your project and team needs' },
    { step: '2', title: 'Review Profiles', description: 'We match you with pre-vetted candidates within 48 hours' },
    { step: '3', title: 'Interview & Select', description: 'Interview candidates and select your perfect fit' },
    { step: '4', title: 'Start Building', description: 'Onboard developers and start your project immediately' }
  ];

  return (
    <>
      {/* Meta Tags for SEO */}
      <Helmet>
        <title>Hire Developers | Novaseeds Technologies</title>
        <meta name="description" content="Hire top frontend, backend, mobile, and full-stack developers with Novaseeds Technologies. Pre-vetted experts, flexible engagement models, and transparent pricing." />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          {/* Hero Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
            <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">HIRE DEVELOPERS</span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 mb-6">
              <span className="text-foreground">Hire Expert Developers</span><br />
              <span className="text-gradient">On Demand</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Scale your team with pre-vetted senior developers. Flexible engagement models, transparent pricing, and seamless integration.
            </p>
          </motion.div>

          {/* Developer Types */}
          <section className="mb-32">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">Find Your Perfect Developer</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {developers.map((dev, index) => {
                const Icon = dev.icon;
                return (
                  <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl hover:shadow-lg transition-all hover:-translate-y-1 group">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon size={28} className="text-blue-600" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-medium text-foreground mb-3">{dev.title}</h3>
                    <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-4">{dev.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {dev.skills.map((skill, idx) => (
                        <span key={idx} className="bg-muted border border-[#E2E8F0] rounded-full px-3 py-1 text-xs text-muted-foreground font-medium">{skill}</span>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">Why Hire Through Novaseeds?</h2>
                <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-8">
                  We handle the complexities of hiring, so you can focus on building great products.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }} className="flex items-start space-x-3">
                      <CheckCircle2 size={20} className="text-blue-600 flex-shrink-0 mt-1" />
                      <span className="text-muted-foreground">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <img src="https://images.unsplash.com/photo-1630442923896-244dd3717b35?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Developer working" className="rounded-3xl w-full h-auto shadow-2xl"/>
              </motion.div>
            </div>
          </section>

          {/* Hiring Process */}
          <section className="mb-32">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">How It Works</h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">Get started in 4 simple steps</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {hiringProcess.map((item, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-card backdrop-blur-sm border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl hover:border-blue-200 transition-colors group">
                  <div className="text-6xl font-bold text-blue-600 mb-4">{item.step}</div>
                  <h3 className="text-2xl md:text-3xl font-medium text-foreground mb-3">{item.title}</h3>
                  <p className="text-base md:text-lg leading-relaxed text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-blue-50 border border-blue-200 rounded-3xl p-12 text-center">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">Ready to Build Your Dream Team?</h2>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-8">
              Tell us about your project and we'll match you with the perfect developers
            </p>
            <Link to="/contact" className="inline-block bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)] transition-all hover:scale-105 active:scale-95">
              Hire Developers Now
            </Link>
          </motion.div>
          {/* FAQ */}
       <FAQSection faqs={hiredevelopersFaqs} title="Frequently Asked Questions" />
          {/* Blog Section */}
          <BlogSection categorySlug="trends" title="Latest Market Trends " maxPosts={3} />
        </div>
      </div>
    </>
  );
};
